import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { Alert, Button, Typography } from 'antd'

interface Props {
  children: ReactNode
  label?: string
}

interface State {
  error: Error | null
}

// A failed dynamic import of a lazy route/chunk — happens when a tab was open
// across a deploy and requests a chunk hash that no longer exists. Message text
// differs per browser ("Importing a module script failed" / "Failed to fetch
// dynamically imported module" / "error loading dynamically imported module").
function isChunkLoadError(err: Error | null): boolean {
  const s = `${err?.name ?? ''} ${err?.message ?? ''}`
  return /Importing a module script failed|dynamically imported module|Unable to preload|ChunkLoadError|Loading chunk .* failed/i.test(s)
}

// ErrorBoundary surfaces runtime errors instead of unmounting the
// whole React tree (which would render a whitescreen). Lets us see
// "TypeError: Cannot read X of undefined" right in the UI, with the
// stack — much faster debug loop than chasing console errors.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', this.props.label || '', error, info.componentStack)
    // Stale-chunk after a deploy → reload ONCE to pull the fresh index.html +
    // chunk hashes. Guarded so a genuinely-broken chunk can't loop-reload.
    if (isChunkLoadError(error)) {
      try {
        const last = Number(sessionStorage.getItem('chunkReloadAt') || 0)
        if (Date.now() - last > 10000) {
          sessionStorage.setItem('chunkReloadAt', String(Date.now()))
          window.location.reload()
        }
      } catch {
        window.location.reload()
      }
    }
  }

  render() {
    if (this.state.error) {
      const chunk = isChunkLoadError(this.state.error)
      if (chunk) {
        // Auto-reload already fired (or was suppressed to avoid a loop) — show a
        // clean "please reload" instead of a scary stack trace.
        return (
          <Alert
            type="warning"
            showIcon
            message="Neue Version verfügbar"
            description={
              <>
                <Typography.Paragraph>
                  Die Anwendung wurde aktualisiert. Bitte die Seite neu laden.
                </Typography.Paragraph>
                <Button type="primary" onClick={() => window.location.reload()}>
                  Neu laden
                </Button>
              </>
            }
          />
        )
      }
      return (
        <Alert
          type="error"
          showIcon
          message={`Render-Fehler${this.props.label ? ` (${this.props.label})` : ''}`}
          description={
            <>
              <Typography.Paragraph>
                <Typography.Text code>{this.state.error.message}</Typography.Text>
              </Typography.Paragraph>
              <Typography.Paragraph>
                <details>
                  <summary>Stack-Trace</summary>
                  <pre className="content-pre text-xs">
                    {this.state.error.stack}
                  </pre>
                </details>
              </Typography.Paragraph>
            </>
          }
        />
      )
    }
    return this.props.children
  }
}
